import { useAlbums } from '@/stores/useAlbums.ts'
import { HeyDrive, HeyDriveType } from '@/models/drive.ts'
import { ReactNode } from 'react'
import { getDriveDefaultsFor } from '@/controller/driveDefaults.ts'
import { IconCheck } from '@tabler/icons-react'
import { AlbumPreviewFile } from '@/models/album.ts'
import { useHeyTheme } from '@/contexts/heyTheme.tsx'

// Base interface for any album that can be displayed in the selection
export interface SelectableAlbumItem {
  id: string
  title: string
  thumbnail: string | null
  // Optional preview fields for rect style (used when Album type is passed)
  preview_desktop?: AlbumPreviewFile | null
  preview_mobile?: AlbumPreviewFile | null
  preview_desktop_center_x?: number | null
  preview_desktop_center_y?: number | null
  preview_mobile_center_x?: number | null
  preview_mobile_center_y?: number | null
}

interface AlbumSelectionProps<T extends SelectableAlbumItem> {
  externalAlbums?: Array<T>
  drive?: HeyDrive | null
  driveType?: HeyDriveType
  selectedAlbum: T | null
  setSelectedAlbum: (album: T) => void
  showScrollbar?: boolean
  prependChild?: ReactNode
  disabled?: boolean
  isAlbumChecked?: (albumId: string) => boolean
  // If true, use album data directly from externalAlbums instead of looking up in albums store
  useDirectAlbumData?: boolean
  // Style for album overview: 'circle' (default) or 'rect' (rectangular cards with overlay)
  overviewStyle?: 'circle' | 'rect'
  // Alternative callback for scrolling to album instead of navigation
  onScrollToAlbum?: (album: T) => void
  // Visibility of the album overview: 'both', 'desktop', 'mobile', 'none'
  overviewVisibility?: 'both' | 'desktop' | 'mobile' | 'none'
  // Default image for circle style when album has no thumbnail
  defaultImage?: string | null
}

export default function AlbumSelection<T extends SelectableAlbumItem>({
  externalAlbums,
  drive,
  driveType,
  selectedAlbum,
  setSelectedAlbum,
  showScrollbar,
  prependChild,
  disabled,
  isAlbumChecked,
  useDirectAlbumData,
  overviewStyle = 'circle',
  onScrollToAlbum,
  overviewVisibility = 'both',
  defaultImage,
}: AlbumSelectionProps<T>) {
  const { albums } = useAlbums()
  const { buttonShape } = useHeyTheme()
  const effectiveDriveType = driveType || drive?.drive_type || HeyDriveType.WEDDING
  const isProf = effectiveDriveType === HeyDriveType.PROF
  // If defaultImage is explicitly passed (even as null), respect it. Otherwise auto-resolve from drive type.
  const fallbackImage = defaultImage !== undefined
    ? defaultImage
    : (isProf ? null : getDriveDefaultsFor(effectiveDriveType).previewPicture)
  const isRect = buttonShape === 'rect'
  const cardRounding = isRect ? '' : 'rounded-lg'

  const albumsToRender = externalAlbums || (drive?.albums as unknown as T[]) || []

  // Don't render if visibility is 'none'
  if (overviewVisibility === 'none') {
    return null
  }

  // Determine visibility classes based on theme setting
  const visibilityClass = {
    'both': '',
    'desktop': 'hidden md:block',
    'mobile': 'md:hidden',
  }[overviewVisibility] || ''

  // Handler for album click - uses onScrollToAlbum if provided, otherwise setSelectedAlbum
  function handleAlbumClick(album: T) {
    if (disabled) return
    if (onScrollToAlbum) {
      onScrollToAlbum(album)
    } else {
      setSelectedAlbum(album)
    }
  }

  // Render rect style (rectangular cards with overlay)
  if (overviewStyle === 'rect') {
    return (
      <div className={`py-8 ${visibilityClass}`}>
        <div className='container mx-auto px-4'>
          {/* Mobile: full width stacked */}
          <div className='md:hidden flex flex-col gap-4'>
            {albumsToRender.map(a => {
              const albumData = useDirectAlbumData ? a : (albums[a.id] || a)
              const previewUrl = albumData.preview_mobile?.detail ?? albumData.preview_mobile?.file
                ?? albumData.preview_desktop?.detail ?? albumData.preview_desktop?.file
                ?? albumData.thumbnail ?? fallbackImage
              const focalX = albumData.preview_mobile_center_x ?? albumData.preview_desktop_center_x ?? 0.5
              const focalY = albumData.preview_mobile_center_y ?? albumData.preview_desktop_center_y ?? 0.5

              return (
                <div
                  key={a.id}
                  className={`relative aspect-[16/9] w-full ${cardRounding} overflow-hidden cursor-pointer shadow-sm ${!previewUrl ? 'bg-secondary' : ''}`}
                  onClick={() => handleAlbumClick(a)}
                >
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt={albumData.title}
                      className='w-full h-full object-cover'
                      style={{ objectPosition: `${focalX * 100}% ${focalY * 100}%` }}
                    />
                  )}
                  <div className='absolute inset-0 bg-[black] bg-opacity-40 flex items-center justify-center'>
                    <span className='text-[white] text-xl font-serif'>{albumData.title}</span>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Desktop: grid layout */}
          <div className='hidden md:grid grid-cols-2 lg:grid-cols-3 gap-6'>
            {albumsToRender.map(a => {
              const albumData = useDirectAlbumData ? a : (albums[a.id] || a)
              const previewUrl = albumData.preview_desktop?.detail ?? albumData.preview_desktop?.file
                ?? albumData.preview_mobile?.detail ?? albumData.preview_mobile?.file
                ?? albumData.thumbnail ?? fallbackImage
              const focalX = albumData.preview_desktop_center_x ?? 0.5
              const focalY = albumData.preview_desktop_center_y ?? 0.5

              return (
                <div
                  key={a.id}
                  className={`relative aspect-[4/3] ${cardRounding} overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-shadow ${!previewUrl ? 'bg-secondary' : ''}`}
                  onClick={() => handleAlbumClick(a)}
                >
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt={albumData.title}
                      className='w-full h-full object-cover'
                      style={{ objectPosition: `${focalX * 100}% ${focalY * 100}%` }}
                    />
                  )}
                  <div className='absolute inset-0 bg-[black] bg-opacity-40 flex items-center justify-center hover:bg-opacity-50 transition-colors'>
                    <span className='text-[white] text-2xl font-serif'>{albumData.title}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  // Default circle style - in centered container, but items left-aligned with horizontal scroll
  return (
    <div className={`py-4 ${visibilityClass}`}>
      <div className='container mx-auto'>
        <div className={'flex gap-3 px-2 overflow-x-auto '
            + (showScrollbar ? '' : 'scrollbar-hide ')
            + (disabled ? 'opacity-30 ' : '')}
        >
          { prependChild }
          { albumsToRender.map(a => {
            // Use direct album data for share mode, or look up in albums store for drive mode
            const albumData = useDirectAlbumData ? a : (albums[a.id] || a)
            return <SelectableAlbum
              onClick={() => handleAlbumClick(a)}
              key={a.id}
              album={albumData}
              defaultImage={fallbackImage}
              isSelected={selectedAlbum != null && selectedAlbum.id === a.id}
              isChecked={!!isAlbumChecked?.(a.id)}
            />
          })}
        </div>
      </div>
    </div>
  )
}

function SelectableAlbum({ album, isSelected, onClick, defaultImage, isChecked }: { album: SelectableAlbumItem, isSelected: boolean, onClick: () => void, defaultImage: string | null, isChecked: boolean }) {
  const bgImage = album.thumbnail ? `url(${album.thumbnail})` : defaultImage ? `url(${defaultImage})` : undefined
  return <div className='flex flex-col w-[6rem] items-center cursor-pointer' onClick={onClick}>
    <div className={`bg-cover bg-center rounded-full w-[4rem] h-[4rem] mb-2 relative overflow-hidden ${!bgImage ? 'bg-secondary' : ''}`} style={bgImage ? { backgroundImage: bgImage } : undefined}>
      { isChecked && <div className='w-full h-full bg-primary bg-opacity-75 flex justify-center items-center absolute'>
        <IconCheck size={20} className='text-neutral' />
      </div>}
    </div>
    <div className={
      'rounded-xl text-center text-sm px-2 w-full py-1 border-primary overflow-hidden overflow-ellipsis whitespace-nowrap ' + (isSelected ? 'bg-primary text-neutral' : '')
    }>
      { album.title }
    </div>
  </div>
}
